import { backgroundApiRequest } from './apiMiddleware';

/**
 * Smart API client that automatically chooses between user and background requests
 */
class SmartApiClient {
  constructor() {
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  getToken() {
    // Always get the current token from localStorage to ensure we have the latest
    return localStorage.getItem('token') || null;
  }

  async get(url, config = {}) {
    const token = this.getToken();
    
    // All API calls default to background (non-user-initiated)
    try {
      const response = await backgroundApiRequest(url, { method: 'GET', ...config }, token);
      return { data: response }; // Wrap to match api.get structure
    } catch (error) {
      // For requests that fail due to inactivity, we should fail gracefully
      console.warn('API request failed:', error.message);
      throw error;
    }
  }

  async post(url, data, config = {}) {
    const token = this.getToken();
    
    // Prepare the request body
    let body = data;
    let headers = { ...config.headers };
    
    // Handle different content types
    if (config.headers && config.headers['Content-Type'] === 'multipart/form-data') {
      // For FormData, don't stringify and let fetch set the content-type
      body = data;
      delete headers['Content-Type']; // Let fetch set the boundary
    } else if (data && typeof data === 'object' && !(data instanceof FormData)) {
      // For JSON data (but not FormData)
      body = JSON.stringify(data);
      headers['Content-Type'] = 'application/json';
    }
    
    const requestConfig = {
      method: 'POST',
      body: body,
      headers: headers,
      ...config
    };

    // All API calls default to background (non-user-initiated)
    try {
      const response = await backgroundApiRequest(url, requestConfig, token);
      return { data: response };
    } catch (error) {
      console.warn('API request failed:', error.message);
      throw error;
    }
  }

  async put(url, data, config = {}) {
    const token = this.getToken();
    
    // Prepare the request body
    let body = data;
    let headers = { ...config.headers };
    
    if (data && typeof data === 'object' && !(data instanceof FormData)) {
      body = JSON.stringify(data);
      headers['Content-Type'] = 'application/json';
    }
    
    const requestConfig = {
      method: 'PUT',
      body: body,
      headers: headers,
      ...config
    };

    // All API calls default to background (non-user-initiated)
    try {
      const response = await backgroundApiRequest(url, requestConfig, token);
      return { data: response };
    } catch (error) {
      console.warn('API request failed:', error.message);
      throw error;
    }
  }

  async delete(url, config = {}) {
    const token = this.getToken();
    
    let requestConfig = {
      method: 'DELETE',
      ...config
    };

    // Handle delete requests with data in the body
    if (config.data) {
      requestConfig.body = JSON.stringify(config.data);
      requestConfig.headers = {
        'Content-Type': 'application/json',
        ...config.headers
      };
    }

    // All API calls default to background (non-user-initiated)
    try {
      const response = await backgroundApiRequest(url, requestConfig, token);
      return { data: response };
    } catch (error) {
      console.warn('API request failed:', error.message);
      throw error;
    }
  }
}

const smartApiClient = new SmartApiClient();
export default smartApiClient;